import { useMemo, useState } from "react";
import { useT } from "../../i18n";
import { Abschnitt, Leersatz } from "../../bausteine/Handyformen";
import { Blattvonunten } from "../../bausteine/Blattvonunten";
import { Aufgabenmarke, Leuteliste, aufgabenwort, useVerteilung } from "../../bausteine/Verteilen";
import { useVerteilen } from "../../daten/faelle";
import {
  ZURUECK, aufgabenAus, istBeweglich, istFrei, tagAmHof, uhrzeit, werktagAb,
  type Aufgabe,
} from "../../daten/verteilen";
import { tagKurz, wochentagVon } from "../../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   „Zu verteilen" am Telefon (25.09.2026, 53-verteilen.md).

   Am Schreibtisch wird im Planungsbrett verteilt, im Abschnitt Personal.
   **Das Brett gibt es am Telefon nicht** (Luca, 18.09.2026: *„Das soll auf
   Mobile nicht zur Verfügung stehen"* — `/planung` führt dort auf die
   Startseite). Eine eigene Seite „Verteilen" gibt es auch nicht mehr. Also
   steht, was die Disposition zwischen zwei Anrufen braucht, auf der
   Startseite: die Tage von heute an, je Tag die Aufgaben als dieselben
   Marken wie im Brett — erst was noch niemand hat, dann was vergeben ist.

   Eine Marke antippen öffnet das Blatt: oben der Vorschlag, darunter die
   Leute mit ihrer Lage an dem Tag und dem, was gegen sie spricht. Ein Tipp
   auf den Namen teilt zu; „Rückgängig" steht danach ein paar Sekunden
   unten. Nur für die, die verteilen (Disposition, Verwaltung).
   ══════════════════════════════════════════════════════════════════ */

export function Zuverteilen() {
  const t = useT();
  const heute = tagAmHof(new Date());
  const bis = werktagAb(heute, 4);
  const { data: stand } = useVerteilen(heute, bis);
  const leute = useMemo(() => stand?.leute ?? [], [stand]);
  const verteilung = useVerteilung(leute);
  const [offen, setzeOffen] = useState<string | null>(null);

  const aufgaben = useMemo(() => aufgabenAus(stand?.zeilen ?? [], heute), [stand, heute]);
  const tage = useMemo(() => {
    const liste = [heute];
    for (let i = 1; i <= 4; i += 1) liste.push(werktagAb(heute, i));
    return [...new Set(liste)];
  }, [heute]);

  if (!stand) return null;

  /** Liegengebliebenes steht bei heute, wie im Brett. */
  const amTag = (tag: string) => aufgaben.filter((a) => !a.erledigt
    && (a.tag === tag || (tag === heute && a.tag === ZURUECK)));
  const frei = aufgaben.filter((a) => istFrei(a) && (a.tag === ZURUECK || tage.includes(a.tag)));
  const gewaehlt = offen ? aufgaben.find((a) => a.schluessel === offen) ?? null : null;
  const wochentage = t.liste("kalender.wochentageKurz");

  const tagName = (tag: string) => {
    const kurz = `${wochentage[wochentagVon(tag)] ?? ""} ${tagKurz(tag)}`;
    return tag === heute ? t("verteilen.telefon.heute", { tag: kurz }) : kurz;
  };

  return (
    <Abschnitt
      titel={t("verteilen.telefon.titel")}
      auskunft={frei.length ? t("verteilen.brett.offen", { zahl: frei.length }) : t("verteilen.brett.allesVerteilt")}
    >
      <div className="aufg-telefon">
        {tage.map((tag) => {
          const liste = amTag(tag).filter((a) => !a.fest);
          const reihe = [...liste.filter(istFrei), ...liste.filter((a) => !istFrei(a))];
          if (!reihe.length) return null;
          const n = liste.filter(istFrei).length;
          return (
            <section key={tag} className="aufg-telefon-tag">
              <header>
                <b>{tagName(tag)}</b>
                <span className={n ? "offen" : ""}>
                  {n ? t("verteilen.brett.offen", { zahl: n }) : t("verteilen.brett.allesVerteilt")}
                </span>
              </header>
              <div className="aufg-telefon-marken">
                {reihe.map((a) => (
                  <Aufgabenmarke key={a.schluessel} aufgabe={a} stand={stand} markiert={offen === a.schluessel} mitWer
                    onClick={() => { if (istBeweglich(a)) setzeOffen(a.schluessel); }} />
                ))}
              </div>
            </section>
          );
        })}
        {tage.every((tag) => !amTag(tag).some((a) => !a.fest)) ? <Leersatz>{t("verteilen.telefon.leer")}</Leersatz> : null}
      </div>

      <Blattvonunten
        offen={!!gewaehlt}
        schliessen={() => setzeOffen(null)}
        titel={gewaehlt ? `${aufgabenwort(gewaehlt, t)} · ${gewaehlt.kennung}` : ""}
        unter={gewaehlt ? blattUnter(gewaehlt, tagName, t) : undefined}
      >
        {gewaehlt ? (
          <div className="aufg-telefon-blatt">
            <Leuteliste
              leute={leute}
              tag={gewaehlt.tag}
              stand={stand}
              aufgaben={aufgaben}
              fuer={[gewaehlt]}
              vorschlag={null}
              waehlen={(p) => verteilung.geben([gewaehlt], p.id, () => setzeOffen(null))}
            />
            {gewaehlt.wer ? (
              <button type="button" className="aufg-zurueck"
                onClick={() => verteilung.geben([gewaehlt], null, () => setzeOffen(null))}>
                {t("verteilen.brett.zurueckNiemand")}
              </button>
            ) : null}
          </div>
        ) : null}
      </Blattvonunten>
      {verteilung.band}
    </Abschnitt>
  );
}

function blattUnter(a: Aufgabe, tagName: (tag: string) => string, t: ReturnType<typeof useT>): string {
  return [
    a.tag === ZURUECK ? t("verteilen.zurueckTag") : tagName(a.tag),
    a.art !== "vorbereitung" ? t("verteilen.um", { uhr: uhrzeit(a.zeit) }) : null,
    a.kunde,
    a.amHof ? null : a.ortschaft,
  ].filter(Boolean).join(" · ");
}
